'use client'

import React from 'react'

import { twMerge } from 'tailwind-merge'

import { BadgeV2 } from '../../common/BadgeV2'
import { ContentPanelV2 } from '../../common/ContentPanelV2'
import { DataBlockV2 } from '../../common/DataBlockV2'
import { FileCardV2 } from '../../common/FileCardV2'
import { PageTitleV2 } from '../../common/PageTitleV2'
import { SidebarWidgetV2 } from '../../common/SidebarWidgetV2'
import { SimilarOpportunitiesV2 } from '../../common/SimilarOpportunitiesV2'
import { TabPanelV2 } from '../../common/TabPanelV2'
import { TimelineV2 } from '../../common/TimelineV2'

// ============================================================================
// Base Skeleton Primitive
// ============================================================================

interface SkeletonProps {
  className?: string
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div
    className={twMerge(
      'animate-pulse rounded bg-primary-200 dark:bg-primary-700',
      className,
    )}
  />
)

// ============================================================================
// Main Skeleton Component
// ============================================================================

/**
 * SbirDisplayV2Skeleton - Loading skeleton for SBIR detail page
 *
 * Composes V2 component skeletons to match the actual layout exactly,
 * minimizing layout shift when content loads.
 */
const SbirDisplayV2Skeleton: React.FC = () => {
  return (
    <>
      {/* Header Block - matches SbirDisplayV2 header structure */}
      <div className="mb-6">
        <div className="flex flex-col gap-3">
          {/* Badges Row */}
          <div className="flex items-center gap-2">
            <BadgeV2.Skeleton width="sm" />
            <BadgeV2.Skeleton width="md" />
          </div>

          {/* Title + Actions Row */}
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
            <div className="max-w-4xl flex-1 space-y-1.5">
              {/* Title skeleton */}
              <PageTitleV2.TitleSkeleton width="w-[40rem]" />
              {/* Solicitation ID skeleton - custom layout (label + mono badge) */}
              <div className="flex items-center gap-2">
                <Skeleton className="h-2.5 w-24" />
                <Skeleton className="h-5 w-36 rounded" />
              </div>
            </div>

            {/* Action Buttons skeleton */}
            <div className="flex shrink-0 items-center gap-2">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Data Grid - matches 2x2/4 column layout */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton showSubValue />
        <DataBlockV2.Skeleton />
        <DataBlockV2.Skeleton showSubValue />
      </div>

      {/* Main Layout - matches 12 column grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Main Content Column */}
        <div className="flex flex-col gap-4 lg:col-span-8">
          {/* Tabs skeleton - matches TabPanelV2 structure */}
          <TabPanelV2.ListSkeleton
            tabCount={2}
            tabWidths={['lg', 'md']}
            className="rounded-t-lg border-x border-t border-primary-200 dark:border-primary-700"
          />

          {/* Content Panel skeleton - matches the -mt-4 overlap */}
          <div className="-mt-4 rounded-b-lg border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <ContentPanelV2.Skeleton
              lineCount={10}
              showHeader
              showFooter
              className="border-0 shadow-none"
            />
          </div>

          {/* Divider below tabs */}
          <div className="mt-4 border-t border-primary-200 dark:border-primary-700" />

          {/* Documents Section skeleton - matches exact structure */}
          <div className="mt-3 rounded-xl border border-primary-200 bg-white shadow-sm dark:border-primary-700 dark:bg-primary-900">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-primary-100 px-4 py-3 dark:border-primary-800">
              <div className="flex items-center gap-1.5">
                <Skeleton className="h-3.5 w-3.5" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-5 w-14 rounded-full" />
            </div>
            {/* File cards */}
            <div className="space-y-2.5 p-3.5">
              <FileCardV2.Skeleton />
              <FileCardV2.Skeleton />
              <FileCardV2.Skeleton />
            </div>
          </div>

          {/* Related Opportunities Section skeleton */}
          <div className="mt-6">
            {/* Header skeleton */}
            <Skeleton className="mb-3 ml-1 h-4 w-60" />
            <SimilarOpportunitiesV2.Skeleton />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 lg:col-span-4">
          {/* View Documents Button skeleton */}
          <div className="flex w-full items-center justify-between rounded-lg border border-primary-200 bg-white p-3.5 shadow-sm dark:border-primary-700 dark:bg-primary-900">
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <Skeleton className="h-4 w-4" />
          </div>

          {/* Snapshot Widget skeleton - matches Opportunity Snapshot structure */}
          <SidebarWidgetV2.Skeleton headerWidth="lg">
            <div className="space-y-4">
              {/* Source System */}
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-24" />
                <Skeleton className="h-4 w-20" />
              </div>
              {/* Program Type */}
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
              {/* Agency */}
              <div className="space-y-1">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-4 w-40" />
              </div>
            </div>
          </SidebarWidgetV2.Skeleton>

          {/* Timeline Widget skeleton */}
          <SidebarWidgetV2.Skeleton headerWidth="sm">
            <TimelineV2.Skeleton nodeCount={4} />
          </SidebarWidgetV2.Skeleton>
        </div>
      </div>
    </>
  )
}

SbirDisplayV2Skeleton.displayName = 'SbirDisplayV2Skeleton'

export { SbirDisplayV2Skeleton }
